import { CellContext } from '@tanstack/react-table';
import { TestQuestionRow } from './StepQuestionsResultsTable';
import { useAsync } from 'react-use';
import { SmallPortalTooltip } from '../../../Tooltip';
import { TooltipContent, TooltipTitle } from '../../Progress/Tooltip';
import { TooltipMarkdownContent } from '../common';
import React from 'react';
import { getCleanMarkdown } from './QuestionTextCell';
import { MainTextTruncateWrapper, WrappedText } from './QuestionTextCell';

export default function QuestionAnswerCell(
  props: CellContext<TestQuestionRow, unknown>
) {
  const maxHeight = 600;
  const type = props.row.original.answer?.type;
  const stepId = props.row.original.stepId;
  const hasFollowUp = !!props.row.original.section?.followUpContent;
  let content = '';

  if (type === 'text-input') {
    content = props.row.original.answer?.answer ?? '*-*';
  }

  if (type === 'ranking') {
    const options = props.row.original.answer?.answer ?? [];
    content =
      options
        ?.map((option, index) => ` ${index + 1}. ${option.content}`)
        .join('\n') ?? '';
  }

  if (type === 'multiple-choice') {
    const options = props.row.original.answer?.answer ?? [];
    content =
      options
        ?.map((option, index) => ` ${index + 1}. ${option.content}`)
        .join('\n') ?? '';
  }

  if (type === 'multiple-rating') {
    const answers = props.row.original.answer?.answer ?? [];
    content =
      answers
        ?.map((answer, index) => ` ${index + 1}. ${answer?.label ?? ' - '}`)
        .join('\n') ?? '';
  }

  if (type === 'matching') {
    const options =
      props.row.original.section.type === 'matching'
        ? {
            leftItems: props.row.original.section.leftItems,
            rightItems: props.row.original.section.rightItems,
          }
        : { leftItems: [], rightItems: [] };

    const pairs =
      props.row.original.answer?.answer?.map((pair) => ({
        left:
          options.leftItems.find((item) => item.id === pair.leftId)?.content ??
          ' - ',
        right:
          options.rightItems.find((item) => item.id === pair.rightId)
            ?.content ?? ' - ',
      })) ?? [];

    content = pairs
      .map((pair, index) => ` ${index + 1}. ${pair.left} -> ${pair.right}`)
      .join('\n');
  }

  if (type === 'fill-in-the-gap' || type === 'text-template') {
    const answer = props.row.original.answer?.answer ?? [];
    content = `${answer
      .map((blank, index) => ` ${index + 1}. ${blank?.content ?? ' - '}`)
      .join('\n')}`;
  }

  if (type === 'py-editor') {
    content = `${
      // @ts-expect-error - answer comes from simulations
      props.row.original.answer?.answer?.code ??
      props.row.original.answer?.answer ??
      '*-*'
    }`;
  }
  if (type === 'llm-editor') {
    content = `${
      // @ts-expect-error - answer comes from simulations
      props.row.original.answer?.answer?.prompt ??
      props.row.original.answer?.answer ??
      '*-*'
    }`;
  }
  if (type === 'py-playground') {
    content = `${
      props.row.original.answer?.answer?.code ?? '*-*'
    }\n\n# Output\n\n${props.row.original.answer?.answer?.output ?? '*-*'}`;
  }

  if (type === 'llm-playground') {
    content = `${
      props.row.original.answer?.answer?.output ?? '*-*'
    }\n\n# Prompt\n\n${props.row.original.answer?.answer?.prompt ?? '*-*'}`;
  }

  if (type === 'matrix-ranking') {
    const answer = props.row.original.answer?.answer ?? [];
    content = `${answer
      .map(
        (item, index) =>
          ` ${index + 1}. ${item?.x}, ${item?.y}, ${item?.note ?? ' - '}`
      )
      .join('\n')}`;
  }

  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const cleanMarkdown = useAsync(() => getCleanMarkdown(content), [content]);
  const onRefChange = React.useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        if (ref.scrollHeight > maxHeight) {
          setIsOverflowing(true);
        } else {
          setIsOverflowing(false);
        }
      }
    },
    [maxHeight]
  );

  const uniqueId = `scenario-${stepId}-table-answer-${props.row.original.answer?.id}`;
  return (
    <>
      <WrappedText data-tooltip-id={uniqueId}>
        {cleanMarkdown?.value || '-'}
      </WrappedText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <MainTextTruncateWrapper
            ref={onRefChange}
            $isOverflowing={isOverflowing}
            maxHeight={maxHeight}
          >
            <TooltipTitle>Answer</TooltipTitle>
            <TooltipMarkdownContent md={`${content}`} />
          </MainTextTruncateWrapper>
          {hasFollowUp && (
            <>
              <TooltipTitle style={{ marginTop: 16 }}>
                Follow-up Answer
              </TooltipTitle>
              <TooltipMarkdownContent
                md={`${props.row.original.answer?.followUpAnswer ?? '*-*'}`}
              />
            </>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}
